import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewInit, ViewChild, ViewChildren, QueryList, DestroyRef, Injector, afterNextRender, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subscription, BehaviorSubject, skip, startWith } from 'rxjs';
import { inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CdkTextareaAutosize, TextFieldModule } from '@angular/cdk/text-field';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';

import { RentalInterruption, RentalLifecycle, SafePeriod, Sales_order_l, interruptionInProgress, lineNetTaxx, roundHalfAway } from '@app/shared/_models/sales';
import { OffHireDialogComponent, OffHireDialogData } from '../off-hire-dialog/off-hire-dialog.component';
import { RentalPlanningDialogComponent } from '@app/sales/rental-billing/planning/rental-planning-dialog.component';
import type { PlanningDialogData } from '@app/sales/rental-billing/rental-dialogs.contract';
import { ActionBarStatus } from '@app/shared/_models/common';
import { Product, RentalTerms, BillingTriggerEvent } from '@app/shared/_models/product';
import { ScanResult, splitQtyPrefix, looksLikeFreeText, scanLabel, scanRejection } from '@app/shared/_models/scan';
import { SearchProductComponent } from '@app/shared/search-product/search-product.component';
import { SmartSelectComponent } from '@app/shared/smart-select/smart-select.component';
import { ConfigurationComponent } from '@app/sales/configuration/configuration.component';
import { QuotationService } from '@app/sales/quotation/quotation.service';
import { ProductsService } from '@app/sales/products/products.service';
import { AlertService } from '@app/shared/alert.service';
import { FormatCurrencyPipe } from '@app/shared/pipes/format-currency.pipe';
import { C_CUF_CONFIGURATION, C_CUF_CONFIGURATION_VALUE, C_VALUES_RENTAL_REASONS, isOrderLineEditable, SalesOrderState, OrderLineType } from '@app/shared/constants';
import { ValuesService } from '@app/common/values/values.service';
import { businessDayOf, todayStr } from '@app/shared/time';
import { frDay } from '@app/sales/rental-billing/rental-days';
import { round_currency } from '@app/shared/util';
import { DatePipe } from '@angular/common';
import { RentalDirectionComponent } from '@app/shared/rental-direction/rental-direction.component';

type LineDelivery = { id: number; realdatdel?: string | null; plandatdel?: string | null };
interface RentIndex {
  lines: FormArray | undefined; value: unknown; count: number; deliveries: unknown;
  rentLines: { index: number; control: AbstractControl }[];
  deliveryByLine: Map<number, LineDelivery>;   // premier BL qui pose la ligne (quantité > 0)
  returnByLine: Map<number, LineDelivery>;     // premier BL qui la reprend (−1 sur la même ligne, JP 29/10)
  returned: Set<AbstractControl>; // matériel repris : cycle.return_realdatdel ou BL de reprise signé
}

// Premier BL non annulé qui porte la ligne, en pose (+) et en reprise (−) ; la reprise n'est plus une ligne à apparier.
function buildRentIndex(lines: FormArray | undefined, value: unknown, deliveries: unknown): RentIndex {
  const controls = lines?.controls ?? [];
  const deliveryByLine = new Map<number, LineDelivery>();
  const returnByLine = new Map<number, LineDelivery>();
  if (Array.isArray(deliveries)) {
    for (const d of deliveries) {
      if (d.mode < 0) continue;
      for (const item of Array.isArray(d.lnk_deliveryl) ? d.lnk_deliveryl : []) {
        const q = Number(item.qty);
        const m = q > 0 ? deliveryByLine : q < 0 ? returnByLine : null;
        if (m && !m.has(item.id_order_l)) m.set(item.id_order_l, { id: d.id, realdatdel: d.realdatdel, plandatdel: d.plandatdel });
      }
    }
  }
  const rentLines = controls.map((control, index) => ({ index, control })).filter(({ control }) => control.get('rental_terms_snapshot')?.value != null || control.get('rental_terms')?.value != null || control.get('data')?.value?.hors_contrat === true);
  const returned = new Set<AbstractControl>();
  for (const { control: c } of rentLines) {
    const id = c.get('id')?.value;
    if (id > 0 && c.get('typ')?.value === OrderLineType.RENTAL && (c.get('rental_lifecycle')?.value?.return_realdatdel || returnByLine.get(id)?.realdatdel)) returned.add(c);
  }
  return { lines, value, count: controls.length, deliveries, rentLines, deliveryByLine, returnByLine, returned };
}

@Component({
  selector: 'app-rentgeneral',
  templateUrl: './rentgeneral.component.html',
  styleUrls: ['./rentgeneral.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DatePipe, FormsModule, ReactiveFormsModule, TextFieldModule, FormatCurrencyPipe, MatButtonModule, MatIconModule, MatTooltipModule, RouterLink, SearchProductComponent, SmartSelectComponent, RentalDirectionComponent],
})
export class RentgeneralComponent implements OnInit, AfterViewInit, OnDestroy {
  private index: RentIndex | null = null;
  @Input() closedLinesLoaded = true; // faux : la pièce a été lue sans ses lignes closes (o=open)
  @Output() loadClosedLines = new EventEmitter<void>();
  public hideReturned = true; // matériels repris masqués à l'affichage seulement : la pièce entière reste chargée et enregistrée
  formGroup!: FormGroup;
  public SalesOrderState = SalesOrderState;

  @Input() loadOrderEventsSubject$!: Observable<FormGroup>;
  @Input() loadCustomerEventsSubject$!: Observable<any>;
  @Input() periods: Array<SafePeriod> = [];
  @Input() actionBarState$!: BehaviorSubject<ActionBarStatus>;
  @Input() group_packagings: boolean = false;

  @Input()
  set is_ht(value: boolean) {
    this._is_ht$.next(value);
  }
  get is_ht(): boolean {
    return this._is_ht$.getValue();
  }
  private readonly _is_ht$ = new BehaviorSubject<boolean>(false);

  @Output('onSubmit') onSubmit: EventEmitter<any> = new EventEmitter();
  @Output() onReload = new EventEmitter<void>();

  @ViewChild('searchProduct') public searchProductElement!: SearchProductComponent;
  @ViewChildren(CdkTextareaAutosize) private memoAutosizes!: QueryList<CdkTextareaAutosize>;

  private eventsSubscription!: Subscription;
  private customerEventsSubscription!: Subscription;
  private configurationSubscription!: Subscription;
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private productsService = inject(ProductsService);
  private alertService = inject(AlertService);
  private quotationService = inject(QuotationService);
  private dialog = inject(MatDialog);
  private valuesService = inject(ValuesService);
  private rentalReasonNames = new Map<number, string>(); // c_values 42, pour l'infobulle de la pastille Suspendu
  private rentalOfferedReasons = new Set<number>(); // motifs aux jours offerts (data.offered = true) : un geste commercial n'est pas une suspension

  readonly C_CUF_CONFIGURATION = C_CUF_CONFIGURATION.toString();
  readonly C_CUF_CONFIGURATION_VALUE = C_CUF_CONFIGURATION_VALUE.toString();

  customer: any;

  // Une reprise n'est plus une ligne (JP 29/10) : elle se programme par un BL −1 sur la ligne louée, depuis le dossier (/rental/:id).
  linetype: FormControl<OrderLineType | null> = new FormControl<OrderLineType | null>(OrderLineType.RENTAL);
  readonly lineTypes = [
    { value: OrderLineType.RENTAL, label: 'Installation' },
  ];

  // Rental term selection state
  selectedProduct: Product | null = null;
  availableRentalTerms: RentalTerms[] = [];
  showRentalTermsSelection: boolean = false;

  readonly periodLabels: { [key: number]: string } = {
    1: 'Heure',
    2: 'Demi-journée',
    3: 'Journée',
    4: 'Semaine',
    5: 'Mois',
    6: 'Trimestre',
    7: 'Année',
  };

  readonly rentalStateLabels: { [key: number]: string } = {
    0: 'Commandé',
    1: 'Livré',
    3: 'Facturé',
    4: 'Arrêté',
    5: 'Récupéré',
  };

  get lines(): FormArray {
    return this.formGroup?.get('lines') as FormArray;
  }

  /** Filtre les lignes qui ont un contrat de location renseigné, avec leur index réel dans le FormArray */
  get rentLines(): { index: number; control: AbstractControl }[] {
    return this.indexed().rentLines;
  }

  // Lignes affichées : sans les matériels repris ni leurs reprises signées tant que hideReturned est vrai (dossier d'établissement sur 10 ans).
  get shownRentLines(): { index: number; control: AbstractControl }[] {
    const ix = this.indexed();
    return this.hideReturned ? ix.rentLines.filter(({ control }) => !ix.returned.has(control)) : ix.rentLines;
  }

  get hiddenReturnedCount(): number {
    return this.indexed().returned.size;
  }

  // Index recalculés une fois par état du formulaire, jamais par ligne affichée : sinon O(lignes × BL) à chaque détection de changement.
  private indexed(): RentIndex {
    const lines = this.lines;
    const value = lines?.value;
    const deliveries = this.formGroup?.get('lnk_deliveries')?.value;
    const c = this.index;
    if (c && c.lines === lines && c.value === value && c.count === (lines?.length ?? 0) && c.deliveries === deliveries) return c;
    this.index = buildRentIndex(lines, value, deliveries);
    return this.index;
  }

  get isLineEditable(): boolean {
    return isOrderLineEditable(this.formGroup?.value?.state, this.formGroup?.value?.doc_type);
  }

  ngOnInit(): void {
    this.eventsSubscription = this.loadOrderEventsSubject$.subscribe((fg: FormGroup) => {
      if (!fg) return;
      this.formGroup = fg;
      this.customer = fg.get('lnk_customer')?.value;
      this.clearSelection();
    });
    this.valuesService
      .getValues$(C_VALUES_RENTAL_REASONS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list: any) => (Array.isArray(list) ? list : []).forEach((v: any) => {
          if (v?.id == null) return;
          this.rentalReasonNames.set(v.id, v.name);
          if (v.data?.offered === true) this.rentalOfferedReasons.add(v.id);
        }),
        error: () => {}, // l'infobulle se passe alors du libellé du motif
      });
  }

  ngAfterViewInit(): void {
    this.customerEventsSubscription = this.loadCustomerEventsSubject$.pipe(skip(1)).subscribe({
      next: (customer: any) => {
        this.customer = customer;
      },
    });

    // La CDK mesure un mémo à la création de sa ligne, avant que le tableau ait ses largeurs finales : un mémo replié y perdait une ligne.
    this.memoAutosizes.changes
      .pipe(startWith(null), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => afterNextRender(() => this.memoAutosizes.forEach((a) => a.resizeToFitContent(true)), { injector: this.injector }));

    // //       //  FOR DEV
    // setTimeout(() => {
    // 	this.linetype.setValue(6);
    // 	const inputEl = this.searchProductElement.searchProductInput()?.nativeElement;
    // 	if (inputEl) {
    // 		inputEl.value = '6393';
    // 		inputEl.dispatchEvent(new Event('input'));
    // 		this.searchProductElement.blurEmitter.emit('6393');
    // 	}
    // },70);
  }

  ngOnDestroy(): void {
    this.eventsSubscription?.unsubscribe();
    this.customerEventsSubscription?.unsubscribe();
    this.configurationSubscription?.unsubscribe();
  }

  // --- Search product handlers ---

  // Même contrat que quotationgeneral : la lecture part telle quelle à /search/scan ; un texte libre non reconnu est ignoré ici
  // (la recherche par nom vit dans app-search-product).
  blurEmitterSearchProduct(searchProduct: string): void {
    if (!searchProduct) return;
    const { raw } = splitQtyPrefix(searchProduct.toString().trim());
    if (!raw) return;

    const pricelists = this.customer?.data?.pricelists ?? this.customer?.lnk_partnerprices?.map((spp: { id: any }) => spp.id);
    this.productsService.search_scan(raw, pricelists).subscribe({
      next: (res: ScanResult) => {
        const rejection = scanRejection(res.scan, raw);
        if (res.ppv?.lnk_product) {
          const product = Product.from(res.ppv.lnk_product);
          this.handleProductForRental(product);
        } else if (rejection) {
          this.alertService.warn(rejection);
          this.searchProductElement.focus();
        } else if (!looksLikeFreeText(res.scan, raw)) {
          this.alertService.warn('Code barre non trouvé : ' + scanLabel(res.scan, raw));
          this.searchProductElement.focus();
        }
      },
      error: e => {
        this.alertService.error('ERREUR Serveur : ' + e.error);
        this.searchProductElement.focus();
      },
    });
  }

  selectProductEmitter(data: any): void {
    if (!data) return;
    const productId = data.hasOwnProperty('ppv') ? data.ppv?.product : data.id;
    if (!productId) return;
    this.productsService.getProductById(productId).subscribe({
      next: (raw: any) => {
        const fullProduct = Product.from(raw);
        this.handleProductForRental(fullProduct);
      },
      error: e => {
        this.alertService.error('ERREUR Serveur : ' + e.error);
        this.searchProductElement.focus();
      },
    });
  }

  private handleProductForRental(product: Product): void {
    if (!product.lnk_rental_terms?.length) {
      this.alertService.warn("Ce produit n'a pas de contrat de location");
      this.searchProductElement.focus();
      return;
    }
    this.selectedProduct = product;
    this.availableRentalTerms = product.lnk_rental_terms;
    this.showRentalTermsSelection = true;
  }

  // --- Rental term selection ---

  isRentalTermInError(rt: RentalTerms): boolean {
    if (rt.error) return true;
    return rt.tiered_pricing?.some(tp => !!tp.fse_lpp && tp.tarifref == null) ?? false;
  }

  calcTtc(taxx: number | undefined, vatRate: number | undefined): number {
    const rate = typeof vatRate === 'number' ? vatRate : parseFloat(vatRate as any) || 0;
    const ht = typeof taxx === 'number' ? taxx : parseFloat(taxx as any) || 0;
    return round_currency(ht * (1 + rate / 100));
  }

  // Choix du contrat de location
  selectRentalTerm(rt: RentalTerms): void {
    if (!this.isLineEditable) return;

    if (this.isRentalTermInError(rt)) {
      this.alertService.error(`Impossible de sélectionner ce contrat pour facturation : ${rt.error || 'Tarif de référence LPP manquant ou inactif'}`);
      return;
    }

    const product = this.selectedProduct!;
    console.log('product', product);
    // Durée vide = illimitée (décision de JP du 26/10) : un 1 posé d'office arrêtait la facturation après la première période.
    const rentalLifecycle: RentalLifecycle = {
      asset: null,
      qty: null,
    };

    const vat = rt.vat ?? product.lnk_template?.vat;
    const rawVatRate = rt.vat_rate ?? product.lnk_template?.vat_rate;

    if (vat == null || rawVatRate == null) {
      this.alertService.error('ERREUR : Pas de TVA définie sur le contrat ou le produit');
      return;
    }

    const vatRate = typeof rawVatRate === 'number' ? rawVatRate : parseFloat(rawVatRate as any);
    if (isNaN(vatRate)) {
      this.alertService.error('ERREUR : Taux de TVA invalide sur le contrat ou le produit');
      return;
    }

    // Le prix de base est le prix de la première période du contrat
    const basePricing = rt.tiered_pricing?.find(tp => tp.starting_period === 1) ?? rt.tiered_pricing?.[0];
    // Les prix effectifs (net_taxi et net_taxx) seront choisis dans le dialogue des tarifs de location
    // Initialisés avec les prix du contrat (tiered_pricing)
    const upriceTaxx = basePricing?.uprice_taxx ?? 0;
    const upriceTaxi = round_currency(upriceTaxx * (1 + vatRate / 100));

    let data: any = product.data ? { ...product.data } : {};
    if (product.lnk_template?.data?.cufs && product.lnk_template.data.cufs.hasOwnProperty(C_CUF_CONFIGURATION.toString())) {
      data = { ...data, cufs: { ...data?.cufs, [C_CUF_CONFIGURATION.toString()]: product.lnk_template.data.cufs[C_CUF_CONFIGURATION.toString()] } };
    }

    this.lines.push(
      this.fb.group({
        id: 0,
        id_order: 0,
        seq: this.highestSeq() + 1,
        typ: this.linetype.value,
        product: product.id,
        template: product.template,
        qty: 1,
        product_name: product.name,
        // Écrivait product.partnbloc dans product_barcode pour que le BL imprime une « Réf. » : le serveur la résout
        // désormais lui-même (COALESCE(ppv.barcode, partnbloc)). Le dialogue rend un produit : ppv posé juste après le push.
        ppv: null,
        vat: vat,
        vat_rate: vatRate,
        uprice_taxx: new FormControl<number>(upriceTaxx), // immutable, prix contractuel, permets de quantifier les rabais
        uprice_taxi: new FormControl<number>(upriceTaxi),
        unet_taxx: new FormControl<number>(upriceTaxx), // Prix de la période sans remise ; la facturation relit unet
        unet_taxi: new FormControl<number>(upriceTaxi),
        vat_cat: new FormControl<string>('S'),
        net_taxx: new FormControl<number>(upriceTaxx), // Montant de ligne (qty 1, colisage 1)
        net_taxi: new FormControl<number>(upriceTaxi),
        spl: null,
        packaging_name: '',
        packaging_qtybaseuom: 1,
        data: data,
        memo: '',
        rental_terms_snapshot: new FormControl(rt),
        rental_lifecycle: Sales_order_l.asRentalLifecycleFormGroup(rentalLifecycle),
      }),
    );
    this.patchDefaultPpv(this.lines.at(-1), product.id);

    if (product && product.lnk_template?.data?.cufs && product.lnk_template.data.cufs.hasOwnProperty(C_CUF_CONFIGURATION.toString())) {
      // Configurable : ouvrir la modale, linked products ajoutés après fermeture via callback
      this.openConfigurationDialog(product.lnk_template.data.cufs[C_CUF_CONFIGURATION.toString()], this.lines.at(-1).value, (data: any) => {
        this.addLinkedProducts(rt);
      });
    } else if (product?.data?.cufs && product.data.cufs.hasOwnProperty(C_CUF_CONFIGURATION.toString())) {
      this.openConfigurationDialog(product.data.cufs[C_CUF_CONFIGURATION.toString()], this.lines.at(-1).value, (data: any) => {
        this.addLinkedProducts(rt);
      });
    } else {
      // Pas de configuration : linked products ajoutés immédiatement
      this.addLinkedProducts(rt);
    }

    this.clearSelection();
    this.searchProductElement.focus();
  }

  cancelSelection(): void {
    this.clearSelection();
    this.searchProductElement.focus();
  }

  // Ajoute les produits liés définis dans le contrat
  // NON TESTE
  private addLinkedProducts(rt: RentalTerms): void {
    console.log('[addLinkedProducts] rt', rt);
    const eventName = BillingTriggerEvent.DELIVERY;
    const pricelists = this.customer?.data?.pricelists ?? this.customer?.lnk_partnerprices?.map((spp: { id: any }) => spp.id);
    rt.linked_products
      .filter(lp => lp.billing_trigger?.event_name === eventName)
      .forEach(lp => {
        // takeUntilDestroyed : la réponse arrive un aller-retour réseau plus tard et pousse dans
        // QuotationService.subject, un service RACINE. Si l'écran a été remis à zéro entre-temps
        // (menu « Nouvelle Location », F3), le produit lié atterrirait dans le document NEUF.
        this.productsService
          .search_productpackaging('jpid', lp.product_id, pricelists)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (ppv: any) => {
              if (ppv?.lnk_product) this.quotationService.subject.next({ product: ppv, qty: lp.quantity });
            },
            error: e => this.alertService.error('ERREUR produit lié : ' + e.error),
          });
      });
  }

  private clearSelection(): void {
    this.selectedProduct = null;
    this.availableRentalTerms = [];
    this.showRentalTermsSelection = false;
  }

  deleteLine(lineIndex: number): void {
    const line = this.lines.at(lineIndex);
    if (this.is_configurable_line(line)) {
      line.value.data.cufs[C_CUF_CONFIGURATION_VALUE].forEach((prod_conf: any) => {
        const idx = this.lines.controls.findIndex((l: any) => l.value.seq == prod_conf.seq);
        if (idx > -1) this.lines.removeAt(idx);
      });
    }
    const finalIdx = this.lines.controls.findIndex((l: any) => l.value.seq == line.value.seq);
    if (finalIdx > -1) this.lines.removeAt(finalIdx);
  }

  /**
   * Retourne le FormControl gérant la durée de location dans `rental_lifecycle.qty`.
   * Si `rental_lifecycle` n'est pas encore un FormGroup, il est automatiquement converti.
   * @param control AbstractControl représentant la ligne de commande
   * @returns FormControl pour la saisie de la durée de location
   */
  getDurationControl(control: AbstractControl): FormControl {
    const rl = control.get('rental_lifecycle');
    if (rl instanceof FormGroup) {
      return rl.get('qty') as FormControl;
    }
    const currentVal = rl?.value;
    const fg = Sales_order_l.asRentalLifecycleFormGroup(typeof currentVal === 'object' ? currentVal : null);
    if (control instanceof FormGroup) {
      control.setControl('rental_lifecycle', fg);
      return fg.get('qty') as FormControl;
    }
    return fg.get('qty') as FormControl;
  }

  periodLabel(period: number): string {
    return this.periodLabels[period] ?? '?';
  }

  rentalStateLabel(state: number): string {
    return this.rentalStateLabels[state] ?? '?';
  }

  private lineInterruptions(control: AbstractControl): RentalInterruption[] {
    const list = control.get('rental_lifecycle')?.value?.interruptions;
    return Array.isArray(list) ? list : [];
  }

  // Interruption non annulée qui couvre le jour de Paris : la pastille ne dépend pas du fuseau du poste.
  // Un geste commercial (motif offert) ne fait pas « Suspendu », comme avant le 27/10/2026.
  private runningInterruption(control: AbstractControl): RentalInterruption | undefined {
    const today = todayStr();
    return this.lineInterruptions(control).find(i => !this.rentalOfferedReasons.has(i.reason_id) && interruptionInProgress(i, today));
  }

  hasActiveSuspension(control: AbstractControl): boolean {
    return this.runningInterruption(control) !== undefined;
  }

  getSuspensionTooltip(control: AbstractControl): string {
    const i = this.runningInterruption(control);
    if (!i) return 'Interruption de facturation';
    // Instants stockés relus en jours de Paris ; end = dernier jour non facturé, montré tel quel (jamais son lendemain).
    const start = businessDayOf(i.start);
    const end = businessDayOf(i.end);
    const since = start ? `depuis le ${frDay(start)}` : 'dès la livraison';
    const until = end ? ` jusqu'au ${frDay(end)} inclus` : '';
    const reason = this.rentalReasonNames.get(i.reason_id);
    return `Suspendu ${since}${until}${reason ? ` (${reason})` : ''}`;
  }

  // Le dialogue écrit par évènements 2102 ; à la fermeture le dossier est relu (enregistré d'abord s'il porte une saisie en cours).
  openOffHireDialog(control: AbstractControl): void {
    let changed = false;
    const realdatdel = this.getDeliveryForLine(control)?.realdatdel;
    const data: OffHireDialogData = {
      orderId: Number(this.formGroup?.get('id')?.value) || 0,
      lineId: Number(control.get('id')?.value) || 0,
      productName: control.get('product_name')?.value || 'Article',
      interruptions: this.lineInterruptions(control),
      deliveredDay: realdatdel ? businessDayOf(realdatdel) : null,
      fseModelId: Number(this.formGroup?.get('data')?.value?.fse_id) || null,
      readonly: !this.isLineEditable && this.formGroup?.value?.state !== SalesOrderState.PREPARATION,
      onDecision: () => (changed = true),
    };
    const dialogRef = this.dialog.open(OffHireDialogComponent, { width: '1100px', maxWidth: '95vw', data });
    dialogRef.afterClosed().subscribe(() => {
      if (!changed) return;
      if (this.formGroup?.dirty) this.onSubmit.emit();
      else this.onReload.emit();
    });
  }

  // Le Planning lit le dossier enregistré (une ligne RENTAL hors reprise suffit) ; jamais ce formulaire, qui perd les prescriptions.
  get planningVisible(): boolean {
    return Number(this.formGroup?.get('id')?.value) > 0 && this.rentLines.some(({ control }) => control.get('typ')?.value === OrderLineType.RENTAL && !this.isReprise(control));
  }

  // Même relecture que les interruptions : à la fermeture, le dossier est relu si une décision est passée (enregistré d'abord s'il porte une saisie).
  openPlanningDialog(): void {
    let changed = false;
    const data: PlanningDialogData = { orderId: Number(this.formGroup?.get('id')?.value) || 0, onDecision: () => (changed = true) };
    const dialogRef = this.dialog.open(RentalPlanningDialogComponent, { width: '96%', height: '94%', maxWidth: '96vw', autoFocus: false, data });
    dialogRef.afterClosed().subscribe(() => {
      if (!changed) return;
      if (this.formGroup?.dirty) this.onSubmit.emit();
      else this.onReload.emit();
    });
  }

  onPriceTaxxChange(control: AbstractControl): void {
    const taxx = parseFloat(control.get('uprice_taxx')?.value) || 0;
    const vatRate = parseFloat(control.get('vat_rate')?.value) || 0;
    const taxi = round_currency(taxx * (1 + vatRate / 100));
    control.get('uprice_taxi')?.setValue(taxi, { emitEvent: false });
    this.setRentalNet(control, taxx, taxi);
  }

  onPriceTaxiChange(control: AbstractControl): void {
    const taxi = parseFloat(control.get('uprice_taxi')?.value) || 0;
    const vatRate = parseFloat(control.get('vat_rate')?.value) || 0;
    const taxx = vatRate ? round_currency(taxi / (1 + vatRate / 100)) : taxi;
    control.get('uprice_taxx')?.setValue(taxx, { emitEvent: false });
    this.setRentalNet(control, taxx, taxi);
  }

  // Pas de remise en location : prix net = prix saisi, montant de ligne = qty × prix (D1, le serveur le recalcule).
  private setRentalNet(control: AbstractControl, taxx: number, taxi: number): void {
    const qty = Number(control.get('qty')?.value) || 0;
    const vatRate = parseFloat(control.get('vat_rate')?.value) || 0;
    const net_taxx = lineNetTaxx(qty, taxx);
    control.get('unet_taxx')?.setValue(taxx, { emitEvent: false });
    control.get('unet_taxi')?.setValue(taxi, { emitEvent: false });
    control.get('net_taxx')?.setValue(net_taxx, { emitEvent: false });
    control.get('net_taxi')?.setValue(roundHalfAway(net_taxx * (1 + vatRate / 100), 2), { emitEvent: false }); // arrondi du serveur, pas round_currency
  }

  onTp2PriceTaxxChange(tp2: any, control: AbstractControl, val: any): void {
    const taxx = parseFloat(val) || 0;
    tp2.uprice_taxx = taxx;
  }

  onTp2PriceTaxiChange(tp2: any, control: AbstractControl, val: any): void {
    const taxi = parseFloat(val) || 0;
    const vatRate = parseFloat(control.get('vat_rate')?.value) || 0;
    tp2.uprice_taxx = vatRate ? round_currency(taxi / (1 + vatRate / 100)) : taxi;
  }

  private highestSeq(): number {
    if (!this.lines || this.lines.length === 0) return 0;
    let maxSeq = 0;
    this.lines.controls.forEach(line => {
      const seq = line.get('seq')?.value ?? 0;
      if (typeof seq === 'number' && seq > maxSeq) maxSeq = seq;
      if (line.value?.data?.cufs?.[C_CUF_CONFIGURATION_VALUE] && Array.isArray(line.value.data.cufs[C_CUF_CONFIGURATION_VALUE])) {
        line.value.data.cufs[C_CUF_CONFIGURATION_VALUE].forEach((prod_conf: any) => {
          maxSeq = Math.max(maxSeq, prod_conf.seq);
        });
      }
    });
    return maxSeq;
  }

  // Contrainte à l'écriture (le BL lit le mémo tel quel) : blancs de tête et de fin ôtés, un mémo blanc redevient '' donc sans cadre.
  trimMemo(line: AbstractControl): void {
    const memo = line.get('memo');
    if (typeof memo?.value === 'string' && memo.value !== memo.value.trim()) memo.setValue(memo.value.trim());
  }

  // --- Configuration produit (cuf -1) ---

  is_configurable_line(line: any): boolean {
    return line && line.value && line.value.data && line.value.data.cufs && line.value.data.cufs[C_CUF_CONFIGURATION.toString()];
  }

  // Le cycle n'a plus de parent_order_l_seq (refusé par le serveur) : un composant de configuration se reconnaît à son type.
  is_child_rental_line(line: any): boolean {
    return line?.value?.typ === OrderLineType.COMPONENT || (line?.value?.rental_terms?.parent_order_l_seq ?? 0) > 0;
  }

  // Reprise hors contrat : ligne louée à quantité négative (data.hors_contrat), seul son BL −n existe.
  isReprise(line: AbstractControl): boolean {
    return line.get('typ')?.value === OrderLineType.RENTAL && Number(line.get('qty')?.value) < 0;
  }

  // Premier BL qui pose la ligne (quantité > 0).
  getDeliveryForLine(line: AbstractControl): LineDelivery | null {
    const lineId = line.get('id')?.value;
    if (!lineId || lineId <= 0) return null;
    return this.indexed().deliveryByLine.get(lineId) ?? null;
  }

  // Premier BL qui reprend la ligne (−1 sur elle-même, ou −n pour une reprise hors contrat).
  getReturnForLine(line: AbstractControl): LineDelivery | null {
    const lineId = line.get('id')?.value;
    if (!lineId || lineId <= 0) return null;
    return this.indexed().returnByLine.get(lineId) ?? null;
  }

  // Une reprise hors contrat passe en rouge pâle avant la couleur de configuration ; un composant enfant garde la sienne.
  lineClass(line: AbstractControl): string {
    const child = this.is_child_rental_line(line);
    if (!child && this.isReprise(line)) return 'return_line';
    return this.is_configurable_line(line) ? 'configurable_product_line' : child ? 'component_child_line' : 'normal_line';
  }

  onConfiguration(line: AbstractControl): void {
    if (line?.value?.data?.cufs?.[C_CUF_CONFIGURATION.toString()]) {
      this.openConfigurationDialog(line.value.data.cufs[C_CUF_CONFIGURATION.toString()], line.value, () => {});
    }
  }

  openConfigurationDialog(configpath: string, parent_line: any, cb: Function): void {
    if (!this.isLineEditable) return;
    console.log('value of parent_line =  ', parent_line);
    const dialogRef = this.dialog.open(ConfigurationComponent, {
      disableClose: true,
      width: '80%',
      height: '80%',
      data: { mode: 'DIALOG', configpath: configpath, parent_line: parent_line },
    });
    this.configurationSubscription = dialogRef.afterClosed().subscribe((data: any) => {
      let data_parent_line = parent_line.data ?? {};
      if (!data_parent_line.cufs) {
        data_parent_line.cufs = {};
      }
      console.log('data', data);
      console.log('parent_line', parent_line);
      if (data?.cufs?.[C_CUF_CONFIGURATION_VALUE] && Array.isArray(data.cufs[C_CUF_CONFIGURATION_VALUE])) {
        data_parent_line.cufs[this.C_CUF_CONFIGURATION_VALUE] = [];
        data.cufs[C_CUF_CONFIGURATION_VALUE].forEach((prod_conf: any) => {
          data_parent_line.cufs[this.C_CUF_CONFIGURATION_VALUE].push({
            id_node: prod_conf.id_node,
            order_node: prod_conf.lnk_node_order_val?.id,
            order_node_name: prod_conf.lnk_node_order_val?.name,
            name: prod_conf.name,
            nlevel: prod_conf.nlevel,
            path: prod_conf.path,
            seq: prod_conf.seq,
            product: prod_conf?.lnk_product?.id,
          });
          let line_exist = false;
          this.lines.controls.forEach((control_l, index) => {
            const line = control_l as FormGroup;
            if (line.get('seq')?.value === prod_conf.seq) {
              line_exist = true;
              if (prod_conf.lnk_product == null || prod_conf.lnk_product == 'null') {
                this.lines.removeAt(index);
              } else {
                // La ligne change de produit : son conditionnement suit, sinon la « Réf. » du BL (COALESCE(ppv.barcode,
                // partnbloc)) imprimerait le code-barres d'un autre article — la FK est logique, rien ne le refuse.
                line.patchValue({
                  product: prod_conf.lnk_product.id,
                  ppv: null,
                  template: prod_conf.lnk_product.template,
                  product_name: prod_conf.lnk_product.name,
                  qty: 1,
                });
                this.patchDefaultPpv(line, prod_conf.lnk_product.id);
              }
            }
          });
          if (!line_exist && prod_conf.lnk_product?.id) {
            const newLine = Sales_order_l.asFormGroup({
              id: 0,
              typ: OrderLineType.COMPONENT,
              id_order: 0,
              product: prod_conf.lnk_product.id,
              template: prod_conf.lnk_product.template,
              qty: 1,
              packaging_qtybaseuom: 1,
              product_name: prod_conf.lnk_product.name,
              vat: parent_line.vat,
              vat_rate: parent_line.vat_rate,
              seq: prod_conf.seq,
              uprice_taxi: 0,
              uprice_taxx: 0,
              unet_taxi: 0,
              unet_taxx: 0,
              vat_cat: 'S',
              net_taxi: 0,
              net_taxx: 0,
              rental_terms_snapshot: parent_line.rental_terms_snapshot ?? parent_line.rental_terms?.lnk_rental_terms ?? parent_line.rental_terms ?? null,
              rental_lifecycle: Sales_order_l.screenLifecycle(parent_line.rental_lifecycle) ?? undefined,
              data: {},
            });
            this.patchDefaultPpv(newLine, prod_conf.lnk_product.id); // Composant : produit connu, conditionnement non — le défaut
            const insertIndex = this.lines.controls.findIndex(ctrl => ctrl.value.seq > prod_conf.seq);
            if (insertIndex === -1) {
              this.lines.push(newLine);
            } else {
              this.lines.insert(insertIndex, newLine);
            }
          }
        });
        const parentlineToUpdate = this.lines.controls.find((ctrl: AbstractControl) => ctrl.value.seq === parent_line.seq);
        if (parentlineToUpdate) {
          parentlineToUpdate.patchValue({ data: data_parent_line });
        }
      }

      // 2. Linked products (via callback — forfait livraison etc.)
      cb?.(data);

      // 3. Options / ventes additionnelles (oreiller, draps…) : plus au dossier, elles vont dans sa commande de vente liée (JP 30/10).
      if (data?.selectedSectionProducts?.length) {
        this.alertService.warn("Les ventes additionnelles n'entrent plus au dossier de location : ajoutez-les à sa commande de vente liée, depuis la page du dossier.");
      }
    });
  }

  // Pose sur la ligne le conditionnement PAR DÉFAUT du produit. La location n'expose pas de choix de conditionnement
  // mais la ligne en désigne un quand même (c'est ce que le modèle vend). Asynchrone : un échec laisse ppv à null.
  private patchDefaultPpv(line: AbstractControl, product_id: number | undefined): void {
    this.productsService.default_ppv_id(product_id).subscribe(ppv_id => {
      if (ppv_id) line.patchValue({ ppv: ppv_id });
    });
  }
}
